import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { MsalProvider as MsalProviderCore } from '@azure/msal-react';
import { initializeMsal, msalInstance } from './msal-setup';
import { AuthProgressLoader } from '@/components/auth/auth-progress-loader';
import { logCritical } from '@/utils/errorReporter';
import { useRedirectToAuthIfNoMsalAccount } from '@/auth/use-redirect-to-auth-if-no-msal-account';

const retryInitializeMsal = async (maxAttempts = 3): Promise<void> => {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await initializeMsal();
      return; // Success
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxAttempts) {
        // Wait before retrying (exponential backoff: 1s, 2s, 4s)
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All attempts failed, throw the last error
  throw lastError;
};

const MsalAuthGuard: FC<PropsWithChildren> = ({ children }) => {
  useRedirectToAuthIfNoMsalAccount();
  return children;
};

export const MsalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);

  useEffect(() => {
    retryInitializeMsal(3)
      .then(() => setIsMsalInitialized(true))
      .catch((error) => {
        logCritical('failed-to-initialize-msal', error);
      });
  }, []);

  if (!isMsalInitialized) {
    return <AuthProgressLoader />;
  }

  return (
    <MsalProviderCore instance={msalInstance}>
      <MsalAuthGuard>{children}</MsalAuthGuard>
    </MsalProviderCore>
  );
};
